import React from "react";
import { motion } from "framer-motion";
import { useT } from "@/lib/i18n";
import { AlertTriangle, ShieldOff, ZapOff } from "lucide-react";

const EASE = [0.22, 1, 0.36, 1];
const ICONS = [AlertTriangle, ShieldOff, ZapOff];

export default function Problem() {
    const { t } = useT();
    const points = t("problem.points") || [];
    return (
        <section
            id="problema"
            data-testid="sn-problem"
            className="relative bg-sn-black text-sn-silver py-24 md:py-40 overflow-hidden"
        >
            <div className="absolute inset-0 hero-grid opacity-40" />
            <div className="grain" />
            <div className="relative max-w-[1400px] mx-auto px-6 md:px-10">
                <motion.div
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, amount: 0.4 }}
                    transition={{ duration: 0.9, ease: EASE }}
                    className="flex items-baseline gap-4 mb-14 md:mb-20"
                >
                    <span className="font-mono-e text-[11px] uppercase tracking-[0.3em] text-sn-blue">
                        {t("problem.eyebrow")}
                    </span>
                    <span className="hairline-dark flex-1" />
                </motion.div>

                <motion.h2
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, amount: 0.4 }}
                    transition={{ duration: 0.9, ease: EASE }}
                    className="font-serif text-5xl md:text-7xl lg:text-8xl leading-[0.95] tracking-[-0.02em] max-w-4xl"
                >
                    {t("problem.headline_a")}{" "}
                    <em className="italic text-sn-blue">{t("problem.headline_b")}</em>.
                </motion.h2>

                <motion.p
                    initial={{ opacity: 0, y: 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, amount: 0.4 }}
                    transition={{ duration: 0.9, delay: 0.1, ease: EASE }}
                    className="mt-8 max-w-2xl text-lg text-sn-fog leading-relaxed"
                >
                    {t("problem.body")}
                </motion.p>

                <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-6">
                    {points.map((p, i) => {
                        const Icon = ICONS[i] || AlertTriangle;
                        return (
                            <motion.div
                                key={i}
                                initial={{ opacity: 0, y: 40 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true, amount: 0.3 }}
                                transition={{ duration: 0.8, delay: i * 0.1, ease: EASE }}
                                className="border border-white/10 rounded-md p-7 bg-white/[0.02]"
                            >
                                <Icon size={22} className="text-sn-blue mb-6" strokeWidth={1.5} />
                                <h3 className="font-serif text-3xl text-sn-silver">{p.title}</h3>
                                <p className="mt-3 text-sn-fog leading-relaxed">{p.text}</p>
                            </motion.div>
                        );
                    })}
                </div>
            </div>
        </section>
    );
}
